// Server-seitige Praxen-Suche für die On-Demand-Liste.
//
// Löst das alte "getFullList aller ~7000 Praxen" ab. Die meisten Filter sind
// direkt in einen PocketBase-Filter übersetzbar und laufen als echte
// getList-Pagination. Zwei Bedingungen können NICHT sauber server-seitig
// paginiert werden und laufen deshalb über einen Hybrid-Pfad (Kandidaten
// server-vorgefiltert laden, dann client-seitig filtern/paginieren):
//
//   1. Telefon-Suche: Die Nummern sind in der DB uneinheitlich formatiert
//      ("05 11- 12 19 30", "040 Ð 52 87 33 0"). Ein `telefon ~ "0511"` trifft
//      sie nicht. Nur der Ziffernvergleich (normalizePhone) funktioniert.
//   2. Kontakt-Filter "Kein Kontakt seit X Tagen": Die Inverse von "hat eine
//      Aktivität >= cutoff" lässt sich nicht zuverlässig negieren, weil eine
//      Praxis alte UND neue Aktivitäten haben kann.
//
// KEINE Back-Relations im Suchpfad — siehe Root-Cause-Notiz über
// buildTextClauses. Praxis-bezogene Treffer aus anderen Collections werden
// über flache Queries auf der jeweiligen Collection ermittelt und als
// id-OR eingebettet.

import { isPhoneQuery, normalizePhone } from './format.js';
import {
  PRAXIS_STATUS_DEFAULT,
  PRAXIS_ART_DEFAULT,
  WIRTSCHAFTSMODELL_DEFAULT,
  UNTERNEHMENSFORM_DEFAULT,
} from './praxis.js';

/**
 * Lädt Praxen zu einer Liste von IDs und gibt sie in der übergebenen
 * Reihenfolge zurück. Fehlende IDs (z. B. inzwischen gelöschte Praxen)
 * werden ausgelassen. Für die "zuletzt besucht"-Karten.
 * @param {object} pb
 * @param {string[]} ids
 * @returns {Promise<object[]>}
 */
export async function fetchPraxenByIds(pb, ids) {
  if (!Array.isArray(ids) || ids.length === 0) return [];
  const ors = ids.map((_, i) => `id = {:id${i}}`);
  const params = {};
  ids.forEach((id, i) => {
    params[`id${i}`] = id;
  });
  const filter = pb.filter(ors.join(' || '), params);
  const res = await pb.collection('praxen').getList(1, ids.length, {
    filter,
    requestKey: 'recent-praxen',
  });
  const byId = new Map(res.items.map((p) => [p.id, p]));
  // Reihenfolge der IDs beibehalten (getList sortiert sonst willkürlich).
  return ids.map((id) => byId.get(id)).filter(Boolean);
}

// Filter für ein "OrDefault"-Feld: wenn der gewählte Wert der Default ist,
// müssen auch leere DB-Werte matchen (praxisStatusOrDefault('') === Default).
function orDefaultClause(pb, field, value, defaultValue) {
  if (value === defaultValue) {
    return pb.filter(`(${field} = {:v} || ${field} = "")`, { v: value });
  }
  return pb.filter(`${field} = {:v}`, { v: value });
}

/**
 * Baut den PocketBase-Filter-String aus dem Filter-State — OHNE Textsuche.
 * Die Textsuche läuft getrennt: ein Call je betroffener Collection, danach
 * client-seitiger Merge über die Praxis-IDs (siehe buildTextClauses /
 * ladeTextTreffer).
 * @param {object} pb    PocketBase-Client (für pb.filter-Escaping)
 * @param {object} state Filter-State (siehe searchPraxen)
 * @returns {string}
 */
export function buildPraxenFilter(pb, state) {
  const {
    search = '',
    statusFilter = 'alle',
    praxisArtFilter = 'alle',
    fachrichtungFilter = 'alle',
    wirtschaftsmodellFilter = 'alle',
    unternehmensformFilter = 'alle',
    versorgungsformFilter = 'alle',
    verbundFilter,
    softwareFilter = 'alle',
    abrechnungsstelleFilter = 'alle',
    umsatzFilter = 'alle',
    vertragPartnerFilter,
    vertragStatusFilter,
  } = state;

  // Basis: nur Hauptpraxen (Filialen werden separat aufgeklappt/nachgeladen).
  const parts = ['mutter_praxis = ""'];

  if (statusFilter !== 'alle') {
    parts.push(orDefaultClause(pb, 'status', statusFilter, PRAXIS_STATUS_DEFAULT));
  }
  if (praxisArtFilter !== 'alle') {
    parts.push(orDefaultClause(pb, 'praxis_art', praxisArtFilter, PRAXIS_ART_DEFAULT));
  }
  if (fachrichtungFilter !== 'alle') {
    parts.push(pb.filter('fachgebiet_neu = {:v}', { v: fachrichtungFilter }));
  }
  if (wirtschaftsmodellFilter !== 'alle') {
    parts.push(
      orDefaultClause(pb, 'wirtschaftsmodell', wirtschaftsmodellFilter, WIRTSCHAFTSMODELL_DEFAULT)
    );
  }
  if (unternehmensformFilter !== 'alle') {
    parts.push(
      orDefaultClause(pb, 'unternehmensform', unternehmensformFilter, UNTERNEHMENSFORM_DEFAULT)
    );
  }
  if (versorgungsformFilter !== 'alle') {
    // Plain-Equality wie bei fachgebiet_neu, NICHT orDefaultClause: bei
    // versorgungsform ist leer kein Synonym für "unbekannt", ein Filter auf
    // "unbekannt" darf also keine leeren Werte mitziehen.
    parts.push(pb.filter('versorgungsform = {:v}', { v: versorgungsformFilter }));
  }
  if (softwareFilter !== 'alle') {
    // Multi-Select: ?= matcht "mindestens ein Wert gleich".
    parts.push(pb.filter('software ?= {:v}', { v: softwareFilter }));
  }
  if (abrechnungsstelleFilter !== 'alle') {
    parts.push(pb.filter('abrechnungsstelle = {:v}', { v: abrechnungsstelleFilter }));
  }
  if (umsatzFilter === 'mit') {
    parts.push('(umsatz_goae > 0 || umsatz_uvgoae > 0)');
  } else if (umsatzFilter === 'ohne') {
    parts.push('(umsatz_goae = 0 && umsatz_uvgoae = 0)');
  }

  // Verbund (Multi-Select, Sentinel 'keiner' = leeres Feld, analog Vertrag).
  if (verbundFilter && verbundFilter.size > 0) {
    const ors = [];
    for (const v of verbundFilter) {
      if (v === 'keiner') ors.push('verbund = ""');
      else ors.push(pb.filter('verbund = {:v}', { v }));
    }
    parts.push(`(${ors.join(' || ')})`);
  }
  // Vertrag-Partner (Multi-Select, Sentinel 'keiner' = leeres Feld).
  if (vertragPartnerFilter && vertragPartnerFilter.size > 0) {
    const ors = [];
    for (const v of vertragPartnerFilter) {
      if (v === 'keiner') ors.push('vertrag_partner = ""');
      else ors.push(pb.filter('vertrag_partner = {:v}', { v }));
    }
    parts.push(`(${ors.join(' || ')})`);
  }
  // Vertrag-Status (Multi-Select).
  if (vertragStatusFilter && vertragStatusFilter.size > 0) {
    const ors = [];
    for (const v of vertragStatusFilter) ors.push(pb.filter('vertrag_status = {:v}', { v }));
    parts.push(`(${ors.join(' || ')})`);
  }

  return parts.join(' && ');
}

// Textsuche-Klauseln — KEIN Filter hier enthält eine Back-Relation.
//
// ROOT CAUSE des Live-400 (gemessen am 24.08.2026, korrigiert die Annahme
// aus Commit 3cc22f6 vom 07.07.):
//   Ein einzelner Back-Relation-Term (kontakte_via_praxis.*) im Filter lässt
//   PocketBase 0.28.2 bei Live-Datenmenge in den Query-Timeout laufen. Das
//   Request-Log von pb-live zeigt über 3 Tage 15 langsame Requests, ausnahmslos
//   400er auf `praxen` bei exakt ~30006 ms — also dem --queryTimeout-Default
//   von 30 s. Kein einziger Request liegt zwischen 3 s und 30 s: die Verteilung
//   ist bimodal (Millisekunden oder Timeout), das Profil einer entgleisten
//   Query-Planung, nicht das von Last oder I/O.
//   Gegenprobe: WAL 1,8 MB, keine Backups, Prozess im Leerlauf, beide
//   Instanzen auf v0.28.2, und dieselben Suchen laufen auf pb-test mit einer
//   frischen Live-Kopie in Millisekunden — sobald die Back-Relation fehlt.
//   Die Filial-Suche (Call 1, ohne Back-Relation) taucht im Log kein einziges
//   Mal auf; betroffen ist ausschließlich der Haupt-Call.
//   NICHT die Ursache und deshalb hier auch nicht "gefixt": die Anzahl der
//   Back-Relations (auch eine einzelne reicht), die Länge des Filter-Strings,
//   die Collection-Rules (praxen.listRule ist `@request.auth.id != ""`, also
//   für alle Rollen identisch) und die Rolle des Users.
//
// Konsequenz: Treffer, die an einer anderen Collection hängen, werden über
// eine flache Query auf DIESER Collection gesucht; die gesammelten Praxis-IDs
// gehen als id-OR in den Haupt-Call. Das ist dasselbe Muster, das die
// Filial-Suche schon benutzt, und es ist nachweislich schnell.
export function buildTextClauses(pb, rawQuery) {
  const q = (rawQuery || '').trim();
  if (!q) return null;
  return {
    // Haupt-Klausel: ausschließlich direkte Felder auf praxen.
    haupt: pb.filter(
      '(name ~ {:q} || name_zusatz ~ {:q} || strasse ~ {:q} || plz ~ {:q} || ort ~ {:q} || ' +
        'telefon ~ {:q} || verbund ~ {:q} || verbund_sonstiges ~ {:q})',
      { q }
    ),
    // Filial-Suche: direkte Felder auf den Filialen selbst.
    filialSuche: pb.filter(
      'mutter_praxis != "" && (name ~ {:q} || name_zusatz ~ {:q} || strasse ~ {:q} || ' +
        'plz ~ {:q} || ort ~ {:q} || telefon ~ {:q})',
      { q }
    ),
    // Kontakt-Suche: flache Query auf kontakte. Feldliste deckungsgleich mit
    // den drei Feldern, die vorher als kontakte_via_praxis.* im Haupt-Call
    // standen — telefon/mobil bleiben bewusst draußen, die sind uneinheitlich
    // formatiert und laufen über den Phone-Pfad (siehe Kopf der Datei).
    kontaktSuche: pb.filter('(vorname ~ {:q} || nachname ~ {:q} || email ~ {:q})', { q }),
  };
}

// Drei-Call-Textsuche — keiner der drei Calls enthält eine Back-Relation
// (Begründung siehe Root-Cause-Notiz über buildTextClauses):
//   Call 1  – Filialen direkt suchen → deren mutter_praxis-IDs.
//   Call 2  – Kontakte flach durchsuchen → deren praxis-IDs.
//             Ersetzt exakt das, was vorher kontakte_via_praxis.* im
//             Haupt-Call gemacht hat.
//   Call 3  – Haupt-Call: base && (direkte Felder || id ?= <IDs aus 1 und 2>).
//
// Semantik unverändert gegenüber der Back-Relation: der Haupt-Call trägt über
// `base` weiterhin `mutter_praxis = ""`. Praxis-IDs aus Call 2, die auf eine
// Filiale zeigen, fallen dort genauso heraus wie vorher — eine Filiale mit
// passendem Kontakt hat auch früher nicht ihre Mutter in die Liste gehoben.
//
// Bei vielen IDs wird der id-OR gechunkt und die Teil-Ergebnisse client-seitig
// per id dedupliziert.
async function ladeTextTreffer(pb, base, q) {
  const clauses = buildTextClauses(pb, q);

  // Call 1: matchende Filialen → eindeutige Mutter-IDs.
  const filialen = await pb.collection('praxen').getFullList({
    filter: clauses.filialSuche,
    fields: 'mutter_praxis',
    batch: 500,
    requestKey: 'praxen-text-filialsuche',
  });

  // Call 2: matchende Kontakte → deren Praxis-IDs.
  const kontakte = await pb.collection('kontakte').getFullList({
    filter: clauses.kontaktSuche,
    fields: 'praxis',
    batch: 500,
    requestKey: 'praxen-text-kontaktsuche',
  });

  // Beide Quellen in EINE deduplizierte ID-Menge — ein Treffer, der zugleich
  // Mutter einer passenden Filiale ist und einen passenden Kontakt hat, darf
  // den id-OR nicht doppelt verlängern.
  const idSet = new Set();
  for (const f of filialen) if (f.mutter_praxis) idSet.add(f.mutter_praxis);
  for (const k of kontakte) if (k.praxis) idSet.add(k.praxis);
  const extraIds = [...idSet];

  // Call 3 (ggf. gechunkt): Haupt-Call mit id-OR der gesammelten IDs.
  const byId = new Map();
  const runHaupt = async (idClause) => {
    const textOr = idClause ? `(${clauses.haupt} || ${idClause})` : clauses.haupt;
    const rows = await pb.collection('praxen').getFullList({
      filter: `${base} && ${textOr}`,
      batch: 500,
      requestKey: 'praxen-text-haupt',
    });
    for (const p of rows) if (!byId.has(p.id)) byId.set(p.id, p);
  };

  const CHUNK = 80;
  if (extraIds.length === 0) {
    await runHaupt(null);
  } else {
    // Sequentiell (nicht parallel) — hält die Zahl gleichzeitig offener
    // Queries klein; await serialisiert sauber.
    for (let i = 0; i < extraIds.length; i += CHUNK) {
      const chunk = extraIds.slice(i, i + CHUNK);
      const params = {};
      const ors = chunk.map((id, j) => {
        params[`xid${j}`] = id;
        return `id ?= {:xid${j}}`;
      });
      await runHaupt(pb.filter(`(${ors.join(' || ')})`, params));
    }
  }
  return [...byId.values()];
}

// Client-seitiger Vergleicher für den Hybrid-Pfad. Nur name/plz (Ort-Spalte
// sortiert nach PLZ, wie in Selektionen). Leere Werte ans Ende.
function makeComparator(sort) {
  const desc = sort.startsWith('-');
  const key = desc ? sort.slice(1) : sort;
  const field = key === 'plz' ? 'plz' : 'name';
  return (a, b) => {
    const av = (a[field] || '').toString();
    const bv = (b[field] || '').toString();
    if (!av && !bv) return 0;
    if (!av) return 1;
    if (!bv) return -1;
    const cmp =
      field === 'plz' ? (av < bv ? -1 : av > bv ? 1 : 0) : av.localeCompare(bv, 'de');
    return desc ? -cmp : cmp;
  };
}

const AKTIV_KONTAKT_MS = 24 * 60 * 60 * 1000;

// Jüngstes faellig_am je Praxis → als last_kontakt an ALLE übergebenen Records
// hängen (überschreibend). Für den Kontakt-Filter, der last_kontakt über die
// komplette Kandidatenmenge braucht. Lädt alle Aktivitäten einmal.
async function reicherLetztenKontaktAn(pb, praxen) {
  const aktivitaeten = await pb.collection('aktivitaeten').getFullList({
    fields: 'praxis,faellig_am',
    sort: '-faellig_am',
    batch: 500,
    requestKey: null,
  });
  const latest = new Map();
  for (const a of aktivitaeten) {
    if (!latest.has(a.praxis)) latest.set(a.praxis, a.faellig_am);
  }
  for (const p of praxen) p.last_kontakt = latest.get(p.id) ?? null;
  return praxen;
}

// last_kontakt nur für die sichtbare Ergebnisseite nachladen (Decision:
// Aktivitäten pro Seite anreichern, keine globale Sortierung darauf). Bei
// kleinen Seiten gezielt per praxis-IN-Filter, bei großen Seiten (perPage
// 'alle') einmal alle Aktivitäten laden statt eines riesigen OR-Filters.
async function reicherSeiteLetztenKontaktAn(pb, items) {
  if (items.length === 0) return items;
  let aktivitaeten;
  if (items.length > 100) {
    aktivitaeten = await pb.collection('aktivitaeten').getFullList({
      fields: 'praxis,faellig_am',
      sort: '-faellig_am',
      batch: 500,
      requestKey: null,
    });
  } else {
    const ors = items.map((_, i) => `praxis = {:p${i}}`);
    const params = {};
    items.forEach((p, i) => {
      params[`p${i}`] = p.id;
    });
    aktivitaeten = await pb.collection('aktivitaeten').getFullList({
      filter: pb.filter(ors.join(' || '), params),
      fields: 'praxis,faellig_am',
      sort: '-faellig_am',
      batch: 500,
      requestKey: null,
    });
  }
  const latest = new Map();
  for (const a of aktivitaeten) {
    if (!latest.has(a.praxis)) latest.set(a.praxis, a.faellig_am);
  }
  // Nur setzen, wo noch nicht vorhanden (Kontakt-Pfad hat es schon gesetzt).
  for (const p of items) {
    if (p.last_kontakt == null) p.last_kontakt = latest.get(p.id) ?? null;
  }
  return items;
}

function tageSeit(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / AKTIV_KONTAKT_MS);
}

// Prüft, ob eine Filiale textlich auf die Suche passt — deckungsgleich mit den
// Filial-Feldern in buildPraxenFilter (name/strasse/plz/ort/telefon).
function filialeMatchtText(filiale, q) {
  return (
    (filiale.name || '').toLowerCase().includes(q) ||
    (filiale.strasse || '').toLowerCase().includes(q) ||
    (filiale.plz || '').toLowerCase().includes(q) ||
    (filiale.ort || '').toLowerCase().includes(q) ||
    (filiale.telefon || '').toLowerCase().includes(q)
  );
}

/**
 * Lädt alle Filialen EINER Mutter-Praxis (für manuelles Aufklappen — zeigt
 * dann ALLE Filialen, unabhängig von Filtern). Reichert last_kontakt an.
 */
export async function fetchFilialen(pb, mutterId) {
  const filialen = await pb.collection('praxen').getFullList({
    filter: pb.filter('mutter_praxis = {:id}', { id: mutterId }),
    sort: 'name',
    batch: 500,
    // Eigener Key je Mutter — refetchExpandedFilialen kann mehrere parallel
    // laden; ohne eigenen Key würden sie sich gegenseitig auto-canceln.
    requestKey: `filialen-${mutterId}`,
  });
  await reicherSeiteLetztenKontaktAn(pb, filialen);
  return filialen;
}

// Filialen der sichtbaren Seiten-Mütter in EINEM Query laden und anhängen:
//   _filialenCount    – Anzahl (für den ▶-Indikator)
//   _matchingFilialen – bei aktiver Textsuche die textlich passenden Filialen
//                       (für Auto-Aufklappen), sonst null.
async function reicherFilialenAn(pb, items, textQuery) {
  for (const m of items) {
    m._filialenCount = 0;
    m._matchingFilialen = null;
  }
  if (items.length === 0) return items;
  // Bei vielen Müttern (perPage 'alle') würde ein OR-Filter mit hunderten
  // Termen die PB-Filter-Länge sprengen (400). Dann einmal alle Filialen laden
  // und lokal zuordnen. Bei kleinen Seiten gezielt per OR.
  const idSet = new Set(items.map((p) => p.id));
  let filialen;
  if (items.length > 80) {
    const alleFilialen = await pb.collection('praxen').getFullList({
      filter: 'mutter_praxis != ""',
      sort: 'name',
      batch: 500,
      requestKey: null,
    });
    filialen = alleFilialen.filter((f) => idSet.has(f.mutter_praxis));
  } else {
    const ors = items.map((_, i) => `mutter_praxis = {:m${i}}`);
    const params = {};
    items.forEach((p, i) => {
      params[`m${i}`] = p.id;
    });
    filialen = await pb.collection('praxen').getFullList({
      filter: pb.filter(ors.join(' || '), params),
      sort: 'name',
      batch: 500,
      requestKey: null,
    });
  }
  if (filialen.length === 0) return items;
  await reicherSeiteLetztenKontaktAn(pb, filialen);
  const byMutter = new Map();
  for (const f of filialen) {
    const liste = byMutter.get(f.mutter_praxis);
    if (liste) liste.push(f);
    else byMutter.set(f.mutter_praxis, [f]);
  }
  const q = (textQuery || '').trim().toLowerCase();
  for (const m of items) {
    const kinder = byMutter.get(m.id) ?? [];
    m._filialenCount = kinder.length;
    if (q) m._matchingFilialen = kinder.filter((f) => filialeMatchtText(f, q));
  }
  return items;
}

/**
 * Führt die Praxen-Suche aus und liefert ein einheitliches Ergebnis-Objekt:
 *   { items, page, perPage, totalItems, totalPages, mode }
 * mode: 'server' (echte Pagination) | 'hybrid' (client-seitig).
 *
 * @param {object} pb
 * @param {object} args
 * @param {object} args.state    Filter-State
 * @param {number} args.page     1-basiert
 * @param {number|'alle'} args.perPage
 * @param {string} args.sort     'name' | '-name' | 'plz' | '-plz'
 */
export async function searchPraxen(pb, { state, page = 1, perPage = 20, sort = 'name' }) {
  const q = (state.search || '').trim();
  const phone = isPhoneQuery(q);
  // Freitext (keine Telefonnummer) läuft über den 3-Call-Merge (client) —
  // ein Back-Relation-Term im Filter kippt PB in den Query-Timeout (400),
  // siehe Root-Cause-Notiz über buildTextClauses.
  const hasText = q.length > 0 && !phone;
  const kontaktActive = state.kontaktFilter && state.kontaktFilter !== 'alle';
  const alle = perPage === 'alle';
  // PLZ-Sortierung muss "leere PLZ ans Ende" (asc UND desc) — das kann PB
  // server-seitig bei aufsteigender Sortierung nicht. Deshalb client-seitig.
  const clientSort = sort === 'plz' || sort === '-plz';
  const base = buildPraxenFilter(pb, state);

  // --- Server-Pfad: keine Sonderbedingung → echte Pagination ---
  if (!phone && !kontaktActive && !clientSort && !hasText) {
    const filter = base;
    if (alle) {
      const items = await pb.collection('praxen').getFullList({ filter, sort, batch: 500, requestKey: null });
      await reicherSeiteLetztenKontaktAn(pb, items);
      await reicherFilialenAn(pb, items, q);
      return {
        items,
        page: 1,
        perPage: items.length,
        totalItems: items.length,
        totalPages: 1,
        mode: 'server',
      };
    }
    const res = await pb
      .collection('praxen')
      .getList(page, perPage, { filter, sort, requestKey: null });
    await reicherSeiteLetztenKontaktAn(pb, res.items);
    await reicherFilialenAn(pb, res.items, q);
    return {
      items: res.items,
      page: res.page,
      perPage: res.perPage,
      totalItems: res.totalItems,
      totalPages: res.totalPages,
      mode: 'server',
    };
  }

  // --- Client-Pfad: Kandidaten laden, dann client sortieren/paginieren. ---
  // Freitext → 3-Wege-Merge (direct / kontakte / filial), sonst Basis-Filter.
  // Phone-Query lädt die Basis und filtert unten per Ziffernvergleich.
  let candidates = hasText
    ? await ladeTextTreffer(pb, base, q)
    : await pb.collection('praxen').getFullList({ filter: base, sort: 'name', batch: 500, requestKey: null });

  if (phone) {
    const digits = normalizePhone(q);
    if (!digits) {
      candidates = [];
    } else {
      // Kontakt-Telefonnummern einsammeln, deren Praxis matcht.
      const kontakte = await pb.collection('kontakte').getFullList({
        fields: 'praxis,telefon,mobil',
        filter: 'telefon != "" || mobil != ""',
        batch: 500,
        requestKey: null,
      });
      const trefferPraxisIds = new Set();
      for (const k of kontakte) {
        if (
          normalizePhone(k.telefon).includes(digits) ||
          normalizePhone(k.mobil).includes(digits)
        ) {
          trefferPraxisIds.add(k.praxis);
        }
      }
      candidates = candidates.filter(
        (p) => normalizePhone(p.telefon).includes(digits) || trefferPraxisIds.has(p.id)
      );
    }
  }

  if (kontaktActive) {
    await reicherLetztenKontaktAn(pb, candidates);
    const minTage = Number(state.kontaktFilter);
    candidates = candidates.filter((p) => {
      // "Kein Kontakt seit X Tagen" — nie kontaktierte Praxen erfüllen es immer.
      const tage = tageSeit(p.last_kontakt);
      return tage === null || tage >= minTage;
    });
  }

  candidates.sort(makeComparator(sort));

  const totalItems = candidates.length;
  const items = alle ? candidates : candidates.slice((page - 1) * perPage, (page - 1) * perPage + perPage);
  // last_kontakt für die sichtbare Seite nachladen — außer der Kontakt-Filter
  // hat es ohnehin schon über die ganze Kandidatenmenge gesetzt.
  if (!kontaktActive) await reicherSeiteLetztenKontaktAn(pb, items);
  // Filialen anreichern. Bei Phone-Query kein Text-Surfacing (q ist Nummer).
  await reicherFilialenAn(pb, items, phone ? '' : q);

  if (alle) {
    return { items, page: 1, perPage: totalItems, totalItems, totalPages: 1, mode: 'hybrid' };
  }
  return {
    items,
    page,
    perPage,
    totalItems,
    totalPages: Math.max(1, Math.ceil(totalItems / perPage)),
    mode: 'hybrid',
  };
}
